<template>
	<div v-if="resolvedComponent && isVisible" :id="wrapperAnchorId" :style="wrapperStyle" :class="wrapperCssClass" :data-block-type="block.type" :data-block-id="block.id" data-testid="site-block">
		<div v-if="hasWrapperBgOverlay" :style="wrapperBgOverlayStyle"></div>
		<div :style="hasWrapperBgOverlay ? { position: 'relative', zIndex: 1 } : undefined">
			<component
				:is="resolvedComponent"
				v-bind="blockProps"
			/>
		</div>
	</div>
</template>

<script>
/**
 * SiteBlockRenderer.vue
 * ─────────────────────
 * Public-facing read-only block renderer for the Website SPA.
 * Unlike the admin BlockRenderer, this has no Vuex dependency,
 * no admin variants, and no editing support.
 *
 * Wrapper style props (prefixed with _wrapper) are extracted and
 * applied to an outer div, while remaining props are passed to
 * the block component.
 */
import HeroBlock from '@/websites/cms/blocks/heroBlock/heroBlock.vue';
import RichText from '@/websites/cms/blocks/richText/richText.vue';
import ImageBlock from '@/websites/cms/blocks/imageBlock/imageBlock.vue';
import ButtonBlock from '@/websites/cms/blocks/buttonBlock/buttonBlock.vue';
import DividerBlock from '@/websites/cms/blocks/dividerBlock/dividerBlock.vue';
import SpacerBlock from '@/websites/cms/blocks/spacerBlock/spacerBlock.vue';
import VideoEmbedBlock from '@/websites/cms/blocks/videoEmbedBlock/videoEmbedBlock.vue';
import HtmlBlock from '@/websites/cms/blocks/htmlBlock/htmlBlock.vue';
import ImageGalleryBlock from '@/websites/cms/blocks/imageGalleryBlock/imageGalleryBlock.vue';
import EmbedBlock from '@/websites/cms/blocks/embedBlock/embedBlock.vue';
import PostListBlock from '@/websites/cms/blocks/postListBlock/postListBlock.vue';
import NewsletterSignupBlock from '@/websites/cms/blocks/newsletterSignupBlock/newsletterSignupBlock.vue';
import AdSlotBlock from '@/websites/cms/blocks/adSlotBlock/adSlotBlock.vue';
import SiteLogoBlock from '@/websites/cms/blocks/siteLogoBlock/siteLogoBlock.vue';
import HeadingBlock from '@/websites/cms/blocks/headingBlock/headingBlock.vue';
import PostContentBlock from '@/websites/cms/blocks/postContentBlock/postContentBlock.vue';
import PostTitleBlock from '@/websites/cms/blocks/postTitleBlock/postTitleBlock.vue';
import PostMetaBlock from '@/websites/cms/blocks/postMetaBlock/postMetaBlock.vue';
import PostFeatureImageBlock from '@/websites/cms/blocks/postFeatureImageBlock/postFeatureImageBlock.vue';
import PostExcerptBlock from '@/websites/cms/blocks/postExcerptBlock/postExcerptBlock.vue';
import SocialBlock from '@/websites/cms/blocks/socialBlock/socialBlock.vue';
import NavigationBlock from '@/websites/cms/blocks/navigationBlock/navigationBlock.vue';
import AccordionBlock from '@/websites/cms/blocks/accordionBlock/accordionBlock.vue';
import IconBlock from '@/websites/cms/blocks/iconBlock/iconBlock.vue';
import PdfViewerBlock from '@/websites/cms/blocks/pdfViewerBlock/pdfViewerBlock.vue';
import PricingBlock from '@/websites/cms/blocks/pricingBlock/pricingBlock.vue';
import SocialShareBlock from '@/websites/cms/blocks/socialShareBlock/socialShareBlock.vue';
import BreadcrumbsBlock from '@/websites/cms/blocks/breadcrumbsBlock/breadcrumbsBlock.vue';
import SiteColumnsLayout from './SiteColumnsLayout.vue';
import SiteAccordionContainer from './SiteAccordionContainer.vue';
import { isMobileViewport, isBlockVisible } from './viewport.js';
import { resolveBlockStyle, splitBandStyle } from '@/websites/cms/blocks/editorCore/traits.js';

/**
 * Public block map — MUST cover every type registered in
 * Application/websites/cms/blocks/editorCore/blockRegistry.js, otherwise
 * blocks that render in the editor silently disappear on the published site.
 * Container blocks (ColumnsLayout, AccordionContainerBlock) map to public
 * Site* variants because their editor components depend on editor internals.
 */
var BLOCK_MAP = {
	HeroBlock: HeroBlock,
	RichText: RichText,
	HeadingBlock: HeadingBlock,
	ImageBlock: ImageBlock,
	ImageGalleryBlock: ImageGalleryBlock,
	ButtonBlock: ButtonBlock,
	HtmlBlock: HtmlBlock,
	EmbedBlock: EmbedBlock,
	PostListBlock: PostListBlock,
	NewsletterSignupBlock: NewsletterSignupBlock,
	AdSlotBlock: AdSlotBlock,
	SiteLogoBlock: SiteLogoBlock,
	DividerBlock: DividerBlock,
	SpacerBlock: SpacerBlock,
	VideoEmbedBlock: VideoEmbedBlock,
	PostContentBlock: PostContentBlock,
	PostTitleBlock: PostTitleBlock,
	PostMetaBlock: PostMetaBlock,
	PostFeatureImageBlock: PostFeatureImageBlock,
	PostExcerptBlock: PostExcerptBlock,
	SocialBlock: SocialBlock,
	NavigationBlock: NavigationBlock,
	AccordionBlock: AccordionBlock,
	IconBlock: IconBlock,
	PdfViewerBlock: PdfViewerBlock,
	PricingBlock: PricingBlock,
	SocialShareBlock: SocialShareBlock,
	BreadcrumbsBlock: BreadcrumbsBlock,
	ColumnsLayout: SiteColumnsLayout,
	AccordionContainerBlock: SiteAccordionContainer
};

export default {
	name: 'SiteBlockRenderer',

	props: {
		/** Root band: the outer section paints the background (see App.vue). */
		band: { type: Boolean, default: false },
		block: { type: Object, required: true }
	},

	inject: {
		siteSettings: { default: function () { return {}; } }
	},

	computed: {
		resolvedComponent: function () {
			var component = BLOCK_MAP[this.block.type] || null;
			if (!component && this.block && this.block.type) {
				// Surface missing-mapping problems instead of silently dropping content
				// eslint-disable-next-line no-console
				console.warn('SiteBlockRenderer: no public component mapped for block type "' + this.block.type + '" — block will not render. Add it to BLOCK_MAP.');
			}
			return component;
		},

		/**
		 * Enforces the block's visibility prop (desktopOnly/mobileOnly)
		 * everywhere the renderer is used — page body, header, footer,
		 * and nested containers. Reactive to window resizes.
		 */
		isVisible: function () {
			return isBlockVisible(this.block);
		},

		/**
		 * Props passed to the block component. `style` traits are applied
		 * by this wrapper, never by the block.
		 */
		blockProps: function () {
			return this.block.props || {};
		},

		/**
		 * Resolved trait styles (see traits.js). Shared with the editor's
		 * AdminBlockWrapper so the site matches the canvas exactly.
		 */
		resolvedStyle: function () {
			return resolveBlockStyle(this.block.style, {
				isMobile: isMobileViewport(),
				siteSettings: this.siteSettings
			});
		},

		wrapperStyle: function () { return this.band ? splitBandStyle(this.resolvedStyle).inner : this.resolvedStyle.style; },
		wrapperCssClass: function () { return this.resolvedStyle.cssClass; },
		wrapperAnchorId: function () { return this.resolvedStyle.anchorId || null; },
		hasWrapperBgOverlay: function () { return this.resolvedStyle.hasOverlay; },
		wrapperBgOverlayStyle: function () { return this.resolvedStyle.overlayStyle; }
	}
};
</script>
